import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, ChevronLeft, ChevronRight, Sparkles, Network, Clock,
  Compass, GitMerge, NotebookPen, Link2, Quote, HelpCircle, MessageSquare,
  Lightbulb, Route, Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useGraphData } from "@/hooks/useGraphData";
import { ENTITY_COLORS, ENTITY_TEXT } from "@/lib/entityColors";
import SearchAutocomplete from "@/components/search/SearchAutocomplete";

const Mandala = () => (
  <svg
    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(120vw,820px)] h-[min(120vw,820px)] opacity-[0.05] animate-spin-slow"
    viewBox="0 0 400 400" fill="none" aria-hidden="true"
  >
    {[190, 158, 126, 94, 62, 30].map((r, i) => (
      <circle key={r} cx="200" cy="200" r={r} stroke="#ffb020" strokeWidth={i % 2 ? 0.3 : 0.5} />
    ))}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
      <ellipse key={deg} cx="200" cy="118" rx="11" ry="28" stroke="#ff7360" strokeWidth="0.5"
        transform={`rotate(${deg} 200 200)`} />
    ))}
  </svg>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const Eyebrow = ({ children, color = "#10a37f" }) => (
  <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] mb-3" style={{ color }}>
    {children}
  </div>
);

const FEATURES = [
  { id: "sec-explore", eyebrow: "The traditions", title: "Explore the doorways", blurb: "Step into any knowledge system and trace how everything connects.", Icon: Compass, color: "#6d28d9" },
  { id: "sec-path", eyebrow: "Guided learning", title: "Choose your path", blurb: "Enroll in a tradition, track what you've learned and keep typed notes.", Icon: Route, color: "#f4552f" },
  { id: "sec-ai", eyebrow: "Research & give back", title: "Ask an AI, then contribute", blurb: "Research any idea with sources — and help the shared map grow.", Icon: Sparkles, color: "#e09400" },
  { id: "sec-ask", eyebrow: "Search", title: "Ask & compare", blurb: "Search the whole corpus — or compare two ideas side by side.", Icon: Search, color: "#0d8f6e" },
  { id: "sec-timeline", eyebrow: "Chronology", title: "Walk the timeline", blurb: "Texts and thinkers across five millennia, era by era.", Icon: Clock, color: "#4e6073" },
  { id: "sec-map", eyebrow: "Visualize", title: "See the whole map", blurb: "Pan and zoom the entire tradition as one interactive graph.", Icon: Network, color: "#0596b8" },
];

const GraphVisual = () => {
  const nodes = [
    { l: 8, t: 20, s: 46, k: "knowledge_system", label: "Vedānta" },
    { l: 60, t: 4, s: 34, k: "concept", label: "Ātman" },
    { l: 74, t: 52, s: 30, k: "person", label: "Śaṅkara" },
    { l: 34, t: 62, s: 32, k: "school", label: "Advaita" },
    { l: 46, t: 30, s: 26, k: "text", label: "Gītā" },
  ];
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl bg-[#fdfdfb] border border-[#14140f]/10 overflow-hidden">
      <svg className="absolute inset-0 w-full h-full" aria-hidden="true">
        {[[0, 1], [0, 3], [0, 4], [1, 4], [3, 2], [4, 2]].map(([a, b]) => (
          <line key={`${a}-${b}`}
            x1={`${nodes[a].l + nodes[a].s / 20}%`} y1={`${nodes[a].t + nodes[a].s / 15}%`}
            x2={`${nodes[b].l + nodes[b].s / 20}%`} y2={`${nodes[b].t + nodes[b].s / 15}%`}
            stroke="#14140f" strokeOpacity="0.12" strokeWidth="1.5" />
        ))}
      </svg>
      {nodes.map((n) => (
        <span key={n.label}
          className="absolute rounded-full flex items-center justify-center text-[11px] font-semibold shadow-sm px-2 text-center leading-tight"
          style={{ left: `${n.l}%`, top: `${n.t}%`, minWidth: n.s, height: n.s, background: ENTITY_COLORS[n.k], color: ENTITY_TEXT[n.k] }}>
          {n.label}
        </span>
      ))}
    </div>
  );
};

const TimelineVisual = () => {
  const items = [
    { x: 8, up: true, k: "text", label: "Ṛgveda", date: "~1500 BCE" },
    { x: 30, up: false, k: "person", label: "Buddha", date: "~5th c. BCE" },
    { x: 52, up: true, k: "text", label: "Yoga Sūtras", date: "~2nd c. CE" },
    { x: 74, up: false, k: "person", label: "Śaṅkara", date: "~8th c. CE" },
  ];
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl bg-[#fdfdfb] border border-[#14140f]/10 overflow-hidden px-4">
      <div className="absolute left-0 right-0 top-1/2 h-px bg-[#14140f]/15" />
      {["BCE", "0", "CE", ""].map((t, i) => (
        <div key={i} className="absolute top-1/2 -translate-y-1/2" style={{ left: `${12 + i * 26}%` }}>
          <div className="w-px h-3 bg-[#14140f]/20 mx-auto" />
          <div className="text-[10px] text-[#14140f]/40 mt-1">{t}</div>
        </div>
      ))}
      {items.map((it) => (
        <div key={it.label} className={`absolute -translate-x-1/2 ${it.up ? "bottom-1/2 mb-4" : "top-1/2 mt-4"}`} style={{ left: `${it.x}%` }}>
          <div className="rounded-lg bg-white border border-[#14140f]/10 shadow-sm px-2.5 py-1.5 whitespace-nowrap">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ENTITY_COLORS[it.k] }} />
              <span className="text-xs font-medium text-[#14140f]">{it.label}</span>
            </div>
            <div className="text-[10px] text-[#4e6073] mt-0.5">{it.date}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const { nodes, edges } = useGraphData();
  const railRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    document.title = "Living Wisdom Explorer — Ancient Indian Philosophy";
  }, []);

  // Gently auto-advance the hero cards so all six capabilities are seen (pauses on interaction).
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const iv = setInterval(() => {
      const el = railRef.current;
      if (paused || !el) return;
      const step = (el.firstElementChild?.offsetWidth || 300) + 20;
      const maxLeft = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxLeft - 8) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: step, behavior: "smooth" });
    }, 3200);
    return () => clearInterval(iv);
  }, [paused]);

  const pauseTemporarily = () => {
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), 6000);
  };

  const countOf = (t) => nodes.filter((n) => n.type === t).length;
  const statItems = [
    { key: "knowledge_system", n: countOf("knowledge_system"), label: "Knowledge Systems" },
    { key: "school", n: countOf("school"), label: "Schools" },
    { key: "concept", n: countOf("concept"), label: "Concepts" },
    { key: "text", n: countOf("text"), label: "Texts" },
    { key: "person", n: countOf("person"), label: "Thinkers" },
    { key: "edges", n: edges.length, label: "Connections" },
  ];

  const topSystems = useMemo(() => {
    const degree = {};
    edges.forEach((e) => { degree[e.source] = (degree[e.source] || 0) + 1; degree[e.target] = (degree[e.target] || 0) + 1; });
    return nodes
      .filter((n) => n.type === "knowledge_system")
      .map((n) => ({ ...n, connections: degree[n.id] || 0 }))
      .sort((a, b) => b.connections - a.connections || a.label.localeCompare(b.label))
      .slice(0, 6);
  }, [nodes, edges]);

  const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const slide = (dir) => { pauseTemporarily(); railRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" }); };
  const startCurating = () => (!user ? openAuth() : navigate(user.role === "curator" || user.role === "admin" ? "/curate" : "/explore"));

  return (
    <div className="relative overflow-hidden bg-[#fdfdfb]">
      {/* ── Hero: sliding feature cards ── */}
      <section className="relative px-6 pt-16 pb-14 md:pt-20">
        <Mandala />
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <Eyebrow>Ancient wisdom · Modern inquiry</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-[#14140f]">
            One living map of
            <br />
            <em className="text-[#e5462e]">Indian philosophy</em>
          </h1>
          <p className="text-base md:text-lg text-[#4e6073] mt-5 max-w-xl mx-auto leading-relaxed">
            Learn, research, explore and visualize five millennia of thought. Choose where to begin —
            tap a card to see how it works.
          </p>
        </motion.div>

        {/* Sliding cards rail */}
        <div className="relative max-w-6xl mx-auto mt-10">
          <div className="flex justify-end gap-2 mb-3 pr-1">
            <button onClick={() => slide(-1)} data-testid="carousel-prev" aria-label="Previous"
              className="w-9 h-9 rounded-full bg-white border border-[#14140f]/10 shadow-sm flex items-center justify-center text-[#14140f]/70 hover:text-[#14140f] hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#10a37f]">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => slide(1)} data-testid="carousel-next" aria-label="Next"
              className="w-9 h-9 rounded-full bg-white border border-[#14140f]/10 shadow-sm flex items-center justify-center text-[#14140f]/70 hover:text-[#14140f] hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[#10a37f]">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div
            ref={railRef}
            data-testid="hero-feature-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={() => setPaused(false)}
            onPointerDown={pauseTemporarily}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          >
            {FEATURES.map((f, i) => (
              <motion.button
                key={f.id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
                onClick={() => scrollToId(f.id)}
                data-testid={`feature-card-${f.id}`}
                className="group snap-start shrink-0 w-[262px] sm:w-[300px] text-left bg-white border border-[#14140f]/10 rounded-2xl p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 focus:outline-none focus:ring-2 focus:ring-[#10a37f]"
              >
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl mb-4"
                  style={{ background: `${f.color}1a`, color: f.color }}>
                  <f.Icon className="w-5 h-5" strokeWidth={1.75} />
                </span>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] mb-1.5" style={{ color: f.color }}>
                  {f.eyebrow}
                </div>
                <h3 className="font-serif text-2xl text-[#14140f] leading-tight mb-2">{f.title}</h3>
                <p className="text-sm text-[#4e6073] leading-relaxed">{f.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#14140f]/70 group-hover:text-[#14140f] transition-colors">
                  See how it works <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" />
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Corpus at a Glance ── */}
      <section className="w-full bg-[#f5f4f0] border-y border-[#14140f]/10 py-12 px-6" data-testid="stats-section">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
          {statItems.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05}>
              <div className="flex flex-col items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: ENTITY_COLORS[s.key] || "#10a37f" }} />
                <div className="font-serif text-4xl md:text-5xl text-[#14140f] tracking-tight">{s.n || "—"}</div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-[#4e6073] font-semibold">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── 1. Explore doorways (top 6 by connections) ── */}
      <section id="sec-explore" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <Eyebrow color="#7c3aed">The traditions</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">Enter through a doorway</h2>
              <p className="text-[#4e6073] mt-3 max-w-xl leading-relaxed">
                The most richly connected knowledge systems. Each opens into an encyclopedic hub of its
                schools, concepts, texts and thinkers.
              </p>
            </div>
            <button onClick={() => navigate("/explore")} data-testid="explore-doorways-link"
              className="group inline-flex items-center gap-2 rounded-full bg-[#14140f] text-[#fdfdfb] px-5 py-2.5 text-sm font-medium hover:bg-black transition-colors shrink-0">
              See all {countOf("knowledge_system") || 14} systems
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {topSystems.map((ks, i) => (
              <Reveal key={ks.id} delay={(i % 3) * 0.05}>
                <button onClick={() => navigate(`/explore?node=${ks.id}`)} data-testid={`home-doorway-${ks.id}`}
                  className="group w-full h-full text-left bg-white border border-[#14140f]/10 rounded-xl p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 focus:outline-none focus:ring-2 focus:ring-[#10a37f]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ENTITY_COLORS.knowledge_system }} />
                      <h3 className="font-serif text-xl text-[#14140f] leading-tight truncate">{ks.label}</h3>
                    </div>
                    <span className="text-xs text-[#4e6073] shrink-0">{ks.connections}</span>
                  </div>
                  {ks.description && <p className="text-sm text-[#4e6073] leading-relaxed line-clamp-2">{ks.description}</p>}
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#c23c28] group-hover:gap-2 transition-all">
                    Open hub <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. Choose your path ── */}
      <section id="sec-path" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12 bg-white border-y border-[#14140f]/10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 md:gap-16 items-center">
          <Reveal className="relative h-64 sm:h-72 order-last md:order-first">
            {[
              { type: "Note", Icon: NotebookPen, c: "#ff7360", rot: -5, x: "4%", y: "4%", z: 10, text: "Māyā ≠ illusion, but dependent appearance." },
              { type: "Quote", Icon: Quote, c: "#10a37f", rot: 4, x: "30%", y: "26%", z: 20, text: "“Brahman is real; the world is an appearance.”" },
              { type: "Link", Icon: Link2, c: "#4e6073", rot: -3, x: "8%", y: "50%", z: 30, text: "Upaniṣads — primary source" },
              { type: "Question", Icon: HelpCircle, c: "#7c3aed", rot: 6, x: "40%", y: "58%", z: 40, text: "How does Advaita differ from Viśiṣṭādvaita?" },
            ].map((card) => (
              <div key={card.type} className="absolute w-44 sm:w-56 bg-white border border-[#14140f]/10 rounded-xl shadow-md p-4"
                style={{ left: card.x, top: card.y, zIndex: card.z, transform: `rotate(${card.rot}deg)` }}>
                <div className="flex items-center gap-1.5 mb-1.5" style={{ color: card.c }}>
                  <card.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">{card.type}</span>
                </div>
                <p className="text-sm text-[#14140f]/85 leading-snug">{card.text}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow color="#ff7360">Guided learning</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">Chart your own path</h2>
            <p className="text-[#4e6073] mt-4 leading-relaxed">
              Enroll in a tradition, mark concepts as you learn them, and keep typed notes — thoughts,
              quotes, links and questions — pinned to any entity or path. Your progress and personal
              archive build over time.
            </p>
            <button onClick={() => (user ? navigate("/journey") : openAuth())} data-testid="journey-enroll-button"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[#ff7360] text-[#14140f] px-5 py-2.5 text-sm font-medium hover:bg-[#e85a46] transition-colors">
              {user ? "Open my journey" : "Begin your journey"}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ── 3. AI Research + Contribute back (single horizontal space) ── */}
      <section id="sec-ai" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12 bg-[#f5f4f0] border-y border-[#14140f]/10">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <Eyebrow color="#ffb020">Research & give back</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">Go deeper, then help the map grow</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* AI Research */}
            <Reveal>
              <div className="h-full bg-white border border-[#14140f]/10 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="flex items-center gap-2 text-[#a86400] mb-4">
                  <Sparkles className="w-5 h-5" strokeWidth={1.75} />
                  <span className="text-xs font-semibold uppercase tracking-[0.16em]">AI Research · bring your own key</span>
                </div>
                <h3 className="font-serif text-2xl text-[#14140f] mb-2">Ask an AI, with sources</h3>
                <p className="text-[#4e6073] leading-relaxed">
                  Connect Perplexity, Claude or ChatGPT and research any entity in a streaming,
                  multi-turn chat — then save cited answers to your notes.
                </p>
                <div className="my-6 rounded-xl bg-[#fdfdfb] border border-[#14140f]/10 p-4">
                  <div className="flex items-center gap-2 text-xs text-[#4e6073] mb-2"><MessageSquare className="w-3.5 h-3.5" /> Researching “Ātman”…</div>
                  <div className="space-y-1.5">
                    <div className="h-2 rounded-full bg-[#ff7360]/20 w-11/12" />
                    <div className="h-2 rounded-full bg-[#ff7360]/20 w-4/5" />
                    <div className="h-2 rounded-full bg-[#ffb020]/30 w-2/3" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {["Upaniṣads", "Brahma Sūtras", "Gītā"].map((s) => (
                      <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-[#14140f]/10 text-[#4e6073]">{s}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => (user ? navigate("/ai-keys") : openAuth())} data-testid="ai-research-cta"
                  className="group mt-auto inline-flex items-center gap-2 text-sm font-medium text-[#a86400] hover:gap-3 transition-all">
                  {user ? "Connect a provider" : "Sign in to research"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
            {/* Contribute back */}
            <Reveal delay={0.08}>
              <div className="h-full bg-white border border-[#14140f]/10 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="flex items-center gap-2 text-[#c23c28] mb-4">
                  <GitMerge className="w-5 h-5" strokeWidth={1.75} />
                  <span className="text-xs font-semibold uppercase tracking-[0.16em]">Community Curation</span>
                </div>
                <h3 className="font-serif text-2xl text-[#14140f] mb-2">Help the map grow</h3>
                <p className="text-[#4e6073] leading-relaxed">
                  Suggest new entities and enrichments as you learn. Curators review a queue, refine the
                  graph, and merge duplicates — every contribution is stewarded.
                </p>
                <div className="my-6 space-y-2">
                  {[
                    { Icon: Lightbulb, label: "Suggest: “Pratyabhijñā”", tag: "New concept", c: ENTITY_COLORS.concept },
                    { Icon: Compass, label: "Enrich: Sāṃkhya · date", tag: "Edit", c: ENTITY_COLORS.knowledge_system },
                    { Icon: GitMerge, label: "Merge two “Nyāya” nodes", tag: "Review", c: ENTITY_COLORS.school },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3 rounded-lg bg-[#fdfdfb] border border-[#14140f]/10 px-3 py-2">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: `${row.c}22`, color: row.c }}>
                        <row.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </span>
                      <span className="text-sm text-[#14140f]/85 flex-1 truncate">{row.label}</span>
                      <span className="text-[10px] uppercase tracking-wide text-[#4e6073]">{row.tag}</span>
                    </div>
                  ))}
                </div>
                <button onClick={startCurating} data-testid="curate-cta"
                  className="group mt-auto inline-flex items-center gap-2 text-sm font-medium text-[#c23c28] hover:gap-3 transition-all">
                  {user ? "Start contributing" : "Sign in to contribute"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 4. Ask & compare ── */}
      <section id="sec-ask" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12">
        <Reveal className="max-w-2xl mx-auto text-center">
          <Eyebrow>Search</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">Ask anything — or compare</h2>
          <p className="text-[#4e6073] mt-3 leading-relaxed">
            Search the whole corpus, or name two ideas at once for a side-by-side comparison.
          </p>
          <div className="mt-7">
            <SearchAutocomplete
              testIdPrefix="ask-search"
              placeholder="e.g. Ātman vs Brahman"
              icon={<Sparkles className="w-5 h-5 text-[#a86400] shrink-0" />}
              submitIcon={<ArrowRight className="w-4 h-4" />}
              pillClassName="flex items-center gap-2 bg-white rounded-full shadow-sm border border-[#14140f]/10 pl-5 pr-2 py-2 focus-within:border-[#ff7360] transition-colors"
              buttonClassName="shrink-0 w-10 h-10 rounded-full bg-[#ff7360] text-[#14140f] flex items-center justify-center hover:bg-[#e85a46] transition-colors"
            />
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {["Ātman vs Brahman", "What is Advaita Vedānta?", "What is Anattā?"].map((t) => (
                <button key={t} onClick={() => navigate(`/answers?q=${encodeURIComponent(t)}`)} data-testid="ask-chip"
                  className="text-sm rounded-full border border-[#14140f]/10 bg-[#fdfdfb] px-3.5 py-1.5 text-[#4e6073] hover:border-[#ff7360] hover:text-[#14140f] transition-colors">
                  {t}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── 5. Timeline ── */}
      <section id="sec-timeline" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12 bg-white border-y border-[#14140f]/10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 md:gap-16 items-center">
          <Reveal><TimelineVisual /></Reveal>
          <Reveal delay={0.08} className="md:order-last">
            <Eyebrow color="#4e6073">Chronology</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">Walk five millennia, era by era</h2>
            <p className="text-[#4e6073] mt-4 leading-relaxed">
              A chronological ribbon of texts and thinkers from the Vedic age to the medieval
              commentators — filter by tradition and watch ideas unfold across BCE and CE.
            </p>
            <button onClick={() => navigate("/explore?view=timeline")} data-testid="timeline-view-cta"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#c23c28] hover:gap-3 transition-all">
              <Clock className="w-4 h-4" strokeWidth={1.5} /> Open the Timeline lens <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ── 6. Map ── */}
      <section id="sec-map" className="scroll-mt-24 py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 md:gap-16 items-center">
          <Reveal>
            <Eyebrow color="#06b6d4">Visualize</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] leading-tight">See the whole tradition as one graph</h2>
            <p className="text-[#4e6073] mt-4 leading-relaxed">
              Pan and zoom through an interactive node-link map of {edges.length || "dozens of"}{" "}
              relationships — trace how a concept threads through schools, texts and thinkers.
            </p>
            <button onClick={() => navigate("/explore?view=map")} data-testid="graph-view-cta"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#c23c28] hover:gap-3 transition-all">
              <Network className="w-4 h-4" strokeWidth={1.5} /> Open the Map lens <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
          <Reveal delay={0.08}><GraphVisual /></Reveal>
        </div>
      </section>
    </div>
  );
}
